// components/slice/cart-slice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "./store"; // Adjust path if needed

type CartData = any; // Replace with specific cart structure if available

type CartState = {
  carts: {
    [organisationId: string]: {
      [accessPointId: string]: CartData;
    };
  };
  activeCart: any;
  cartSource?: string | null;
};

const initialState: CartState = {
  carts: {},
  activeCart: null,
  cartSource: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCarts: (state, action) => {
      state.carts = action.payload;
    },
    setActiveCart: (state, action) => {
      state.activeCart = action.payload;
    },
    clearCarts: (state) => {
      state.carts = {};
      state.activeCart = null;
    },
    removeCart: (state, action) => {
      const { organisationId, accessPointId } = action.payload;
      if (state.carts[organisationId]?.[accessPointId]) {
        delete state.carts[organisationId][accessPointId];

        // Clean up empty organisation entry
        if (Object.keys(state.carts[organisationId]).length === 0) {
          delete state.carts[organisationId];
        }
      }
    },
    setCartSource: (state, action) => {
      state.cartSource = action.payload;
    },
  },
});

// ✅ Thunk to update or add a cart
export const updateCart = (
  organisationId: string,
  accessPointId: string,
  cart: CartData
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const { carts } = getState().cart;
    const resolvedOrganisationId =
      organisationId || cart?.profileId || cart?.host?.profileId || cart?.host?.orgId;
    const resolvedAccessPointId = accessPointId || cart?.accessPointId;

    if (!resolvedOrganisationId || !resolvedAccessPointId) return;

    const updatedCarts = {
      ...carts,
      [resolvedOrganisationId]: {
        ...(carts[resolvedOrganisationId] || {}),
        [resolvedAccessPointId]: cart,
      },
    };
    dispatch(setActiveCart(cart));
    dispatch(setCarts(updatedCarts));
  };
};

export const buildCartsByOrgAndAccessPoint = (collections: CartData[] = []) => {
  return collections.reduce((cartMap, collection) => {
    const organisationId =
      collection?.profileId ||
      collection?.host?.profileId ||
      collection?.host?.orgId;
    const accessPointId = collection?.accessPointId;

    if (!organisationId || !accessPointId) return cartMap;

    return {
      ...cartMap,
      [organisationId]: {
        ...(cartMap[organisationId] || {}),
        [accessPointId]: collection,
      },
    };
  }, {} as CartState["carts"]);
};

export const setCartsFromCollections = (collections: CartData[] = []) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setCarts(buildCartsByOrgAndAccessPoint(collections)));
  };
};

// Export actions
export const { setCarts, setActiveCart, clearCarts, removeCart, setCartSource } =
  cartSlice.actions;

// Optional selectors
export const getAllCarts = (state: RootState) => state.cart.carts;

export const getAllCartsList = (state: RootState) => {
  const carts = state.cart?.carts || {};
  return Object.values(carts).flatMap((organisationCarts) =>
    Object.values(organisationCarts || {})
  );
};

export const getAllCartItemsCount = (state: RootState) => {
  return getAllCartsList(state).reduce((total, cart) => {
    const paymentItem = Array.isArray(cart?.paymentItem) ? cart.paymentItem : [];
    return (
      total +
      paymentItem.reduce((itemTotal: number, item: any) => {
        return itemTotal + Number(item?.unit || 0);
      }, 0)
    );
  }, 0);
};

export const getCartSource = (state: RootState, defaultValue: string = "SHOP") => {
  return state.cart?.cartSource ?? defaultValue;
};

export const getCartByOrgAndAccessPoint = (
  state: RootState,
  organisationId: string,
  accessPointId: string,
  cart: any = {}
) => {
  return state.cart?.carts[organisationId]?.[accessPointId] || cart;
};

export const getActiveCart = (state: RootState) => {
  return state.cart?.activeCart || null;
};

// Export reducer
export default cartSlice.reducer;
